import { Button, Card, Elevation, FormGroup, Icon, InputGroup, Slider, FileInput, Callout } from '@blueprintjs/core'
import { toast } from 'botpress/shared'
import React, { FC, useState, useEffect } from 'react'
import api from '~/app/api'
import { subDataStore } from '../../../token-store'

interface Props {
  botId: string
}

interface ChatIconConfig {
  iconUrl?: string
  size?: number
  borderRadius?: number
  borderColor?: string
  enableRipple?: boolean
  rippleRadius?: number
  welcomeBubble?: {
    enabled?: boolean
    message?: string
    delay?: number
    typingSpeed?: number
    fontSize?: number
    fontBold?: boolean
    fontColor?: string
  }
}

const WebchatIconCustomization: FC<Props> = ({ botId }) => {
  const [iconUrl, setIconUrl] = useState('')
  const [iconSize, setIconSize] = useState(70)
  const [borderRadius, setBorderRadius] = useState(50)
  const [borderColor, setBorderColor] = useState('#00AEEF')
  const [enableRipple, setEnableRipple] = useState(false)
  const [rippleRadius, setRippleRadius] = useState(100)
  const [enableWelcomeBubble, setEnableWelcomeBubble] = useState(false)
  const [welcomeMessage, setWelcomeMessage] = useState('')
  const [welcomeDelay, setWelcomeDelay] = useState(1000)
  const [typingSpeed, setTypingSpeed] = useState(50)
  const [welcomeFontSize, setWelcomeFontSize] = useState(11)
  const [welcomeFontBold, setWelcomeFontBold] = useState(false)
  const [welcomeFontColor, setWelcomeFontColor] = useState('#333333')
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string>('')
  const fileInputRef = React.useRef<HTMLInputElement>(null)

  // Get subscription data
  const savedSubData = subDataStore.get() || {}
  const subscription = savedSubData.subscription || ''
  // Check if user has access to advanced features
  // Starter plan: only icon settings
  // Trial, Demo, Pro: all features
  const hasAdvancedFeatures = subscription.toLowerCase() !== 'starter'

  useEffect(() => {
    void loadConfig()
  }, [botId])

  const loadConfig = async () => {
    try {
      setIsLoading(true)
      const { data } = await api.getSecured().get(`/admin/workspace/bots/${botId}/module-config/channel-web`)

      // The response is wrapped in payload by sendSuccess
      const config = data?.payload || data

      if (config?.chatIcon) {
        setIconUrl(config.chatIcon.iconUrl || '')
        setPreviewUrl(config.chatIcon.iconUrl || '')
        setIconSize(config.chatIcon.size || 70)
        setBorderRadius(config.chatIcon.borderRadius !== undefined ? config.chatIcon.borderRadius : 50)
        setBorderColor(config.chatIcon.borderColor || '#00AEEF')
        setEnableRipple(config.chatIcon.enableRipple || false)
        setRippleRadius(config.chatIcon.rippleRadius || 100)

        // Load welcome bubble settings
        if (config.chatIcon.welcomeBubble) {
          setEnableWelcomeBubble(config.chatIcon.welcomeBubble.enabled || false)
          setWelcomeMessage(config.chatIcon.welcomeBubble.message || 'Hello! How can I help you?')
          setWelcomeDelay(config.chatIcon.welcomeBubble.delay || 1000)
          setTypingSpeed(config.chatIcon.welcomeBubble.typingSpeed || 50)
          setWelcomeFontSize(config.chatIcon.welcomeBubble.fontSize || 11)
          setWelcomeFontBold(config.chatIcon.welcomeBubble.fontBold || false)
          setWelcomeFontColor(config.chatIcon.welcomeBubble.fontColor || '#333333')
        }

        setImageError(false)
      }
    } catch (error: any) {
      console.error('Failed to load webchat icon config:', error)
      // Don't show error toast on initial load if config doesn't exist
    } finally {
      setIsLoading(false)
    }
  }

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) {
      return
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/svg+xml', 'image/webp']
    if (!allowedTypes.includes(file.type)) {
      toast.failure('Please select a valid image file (JPEG, PNG, GIF, SVG, or WebP)')
      return
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.failure('File size must be less than 5MB')
      return
    }

    setSelectedFile(file)

    // Create preview URL from file for immediate preview
    const filePreviewUrl = URL.createObjectURL(file)
    setPreviewUrl(filePreviewUrl)
    setImageError(false)
  }

  const handleChooseFileClick = () => {
    fileInputRef.current?.click()
  }

  const removeIcon = () => {
    setIconUrl('')
    setSelectedFile(null)
    setPreviewUrl('')
    setImageError(false)
  }

  const saveConfig = async () => {
    try {
      setIsSaving(true)

      // If there's a selected file, upload it first
      let uploadedIconUrl = iconUrl
      if (selectedFile) {
        const formData = new FormData()
        formData.append('icon', selectedFile)

        const response = await api.getSecured().post(`/admin/workspace/bots/${botId}/media/upload`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        })

        // The response structure is { payload: { iconUrl, filePath } }
        uploadedIconUrl = response.data?.payload?.iconUrl || response.data?.iconUrl

        if (!uploadedIconUrl) {
          throw new Error('No icon URL returned from server')
        }

        // Update state with the uploaded URL
        setIconUrl(uploadedIconUrl)
        setPreviewUrl(uploadedIconUrl)
        setSelectedFile(null)
      }

      // Save the configuration with the uploaded icon URL
      // First, get the existing config
      const { data: existingData } = await api.getSecured().get(`/admin/workspace/bots/${botId}/module-config/channel-web`)
      const existingConfig = existingData?.payload || existingData || {}

      const chatIconConfig: ChatIconConfig = {
        iconUrl: uploadedIconUrl || undefined,
        size: iconSize,
        borderRadius,
        borderColor,
        // Only save advanced features if user has access
        enableRipple: hasAdvancedFeatures ? enableRipple : false,
        rippleRadius: hasAdvancedFeatures ? rippleRadius : 100,
        welcomeBubble: hasAdvancedFeatures ? {
          enabled: enableWelcomeBubble,
          message: welcomeMessage,
          delay: welcomeDelay,
          typingSpeed,
          fontSize: welcomeFontSize,
          fontBold: welcomeFontBold,
          fontColor: welcomeFontColor
        } : {
          enabled: false,
          message: 'Hello! How can I help you?',
          delay: 1000,
          typingSpeed: 50,
          fontSize: 11,
          fontBold: false,
          fontColor: '#333333'
        }
      }

      // Send only the chatIcon field to be merged with existing config
      const payload = {
        chatIcon: chatIconConfig
      }

      await api.getSecured().post(`/admin/workspace/bots/${botId}/module-config/channel-web`, payload)

      toast.success('Webchat icon customization saved successfully!')
    } catch (error: any) {
      console.error('Failed to save webchat icon config:', error)
      toast.failure(`Failed to save configuration: ${error.message || 'Unknown error'}`)
    } finally {
      setIsSaving(false)
    }
  }

  const resetToDefaults = () => {
    setIconUrl('')
    setPreviewUrl('')
    setIconSize(70)
    setBorderRadius(50)
    setBorderColor('#00AEEF')
    setEnableRipple(false)
    setRippleRadius(100)
    setEnableWelcomeBubble(false)
    setWelcomeMessage('Hello! How can I help you?')
    setWelcomeDelay(1000)
    setTypingSpeed(50)
    setImageError(false)
    setSelectedFile(null)
  }

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Left Column - Scrollable Settings */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '24px 42px', borderRight: '1px solid #e1e4e8' }}>
        <div style={{ marginBottom: '24px' }}>
          <h4 style={{ margin: '0 0 8px 0', fontSize: '18px', fontWeight: 600, color: '#333' }}>
            Webchat Icon Customization
          </h4>
          <p style={{ margin: 0, fontSize: '14px', color: '#666', lineHeight: '1.5' }}>
            Customize the appearance of your chatbot icon that appears on your website.
          </p>
        </div>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <Icon icon="refresh" iconSize={24} />
            <p style={{ marginTop: '12px' }}>Loading configuration...</p>
          </div>
        ) : (
          <>
            <div style={{ display: 'grid', gap: '24px' }}>
              {/* Upgrade Notice for Starter Plan */}
              {!hasAdvancedFeatures && (
                <Callout
                  intent="warning"
                  icon="lock"
                  title="Upgrade to Access Advanced Features"
                  style={{ marginBottom: '8px' }}
                >
                  <p style={{ margin: 0, fontSize: '13px', lineHeight: '1.5' }}>
                    You're currently on the <strong>Starter</strong> plan. Upgrade to <strong>Professional</strong> to unlock:
                  </p>
                  <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px', fontSize: '13px' }}>
                    <li>Ripple Effect Animation</li>
                    <li>Welcome Message Bubble</li>
                  </ul>
                </Callout>
              )}

              {/* Icon Settings Card */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa' }}>
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="media" iconSize={16} color="#5C7CFA" />
                  Icon Settings
                </h5>

                {/* Icon Upload */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Chat Icon</span>}
                  helperText="Upload a custom icon image (JPEG, PNG, GIF, SVG, or WebP, max 5MB)"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/gif,image/svg+xml,image/webp"
                    onChange={handleFileSelect}
                    style={{ display: 'none' }}
                  />
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                      text="Choose File"
                      icon="upload"
                      onClick={handleChooseFileClick}
                    />
                    {(iconUrl || selectedFile) && (
                      <Button
                        text="Remove Icon"
                        icon="cross"
                        intent="danger"
                        minimal
                        onClick={removeIcon}
                      />
                    )}
                  </div>
                  {selectedFile && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      Selected: {selectedFile.name}
                    </div>
                  )}
                  {iconUrl && !selectedFile && (
                    <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                      ✓ Icon configured
                    </div>
                  )}
                </FormGroup>

                {/* Icon Size */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Icon Size: {iconSize}px</span>}
                >
                  <Slider
                    min={50}
                    max={80}
                    stepSize={5}
                    labelStepSize={10}
                    value={iconSize}
                    onChange={setIconSize}
                    showTrackFill
                  />
                </FormGroup>

                {/* Border Radius */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Radius: {borderRadius}%</span>}
                  helperText="0% = square, 50% = circle"
                >
                  <Slider
                    min={0}
                    max={50}
                    stepSize={5}
                    labelStepSize={10}
                    value={borderRadius}
                    onChange={setBorderRadius}
                    showTrackFill
                  />
                </FormGroup>

                {/* Border Color */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Border Color</span>}
                >
                  <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                    <input
                      type="color"
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value)}
                      style={{
                        width: '50px',
                        height: '36px',
                        border: '2px solid #ddd',
                        borderRadius: '6px',
                        cursor: 'pointer'
                      }}
                    />
                    <InputGroup
                      placeholder="#00AEEF"
                      value={borderColor}
                      onChange={(e) => setBorderColor(e.target.value)}
                    />
                  </div>
                </FormGroup>
              </Card>

              {/* Ripple Effect Card */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="pulse" iconSize={16} color="#5C7CFA" />
                  Ripple Effect
                </h5>

                {/* Ripple Effect */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Enable Ripple</span>}
                >
                  <label style={{ display: 'flex', alignItems: 'center', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={enableRipple}
                      onChange={(e) => hasAdvancedFeatures && setEnableRipple(e.target.checked)}
                      disabled={!hasAdvancedFeatures}
                      style={{ marginRight: '8px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#333' }}>Enable ripple animation on hover</span>
                  </label>
                </FormGroup>

                {/* Ripple Radius */}
                {enableRipple && hasAdvancedFeatures && (
                  <FormGroup
                    label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Ripple Size: {rippleRadius}%</span>}
                    helperText="Size of the ripple effect relative to icon"
                  >
                    <Slider
                      min={120}
                      max={180}
                      stepSize={10}
                      labelStepSize={20}
                      value={rippleRadius}
                      onChange={setRippleRadius}
                      showTrackFill
                    />
                  </FormGroup>
                )}
              </Card>

              {/* Welcome Bubble Card */}
              <Card elevation={Elevation.ONE} style={{ padding: '20px', backgroundColor: '#f8f9fa', opacity: hasAdvancedFeatures ? 1 : 0.6, position: 'relative' }}>
                {!hasAdvancedFeatures && (
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.7)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '3px',
                    zIndex: 10,
                    cursor: 'not-allowed'
                  }}>
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                      <Icon icon="lock" iconSize={32} color="#999" style={{ marginBottom: '8px' }} />
                      <div style={{ fontSize: '14px', fontWeight: 600, color: '#666' }}>
                        Upgrade to Professional
                      </div>
                    </div>
                  </div>
                )}
                <h5 style={{ margin: '0 0 16px 0', fontSize: '15px', fontWeight: 600, color: '#333', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Icon icon="comment" iconSize={16} color="#5C7CFA" />
                  Welcome Message Bubble
                </h5>

                {/* Welcome Bubble */}
                <FormGroup
                  label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Enable Welcome Bubble</span>}
                >
                  <label style={{ display: 'flex', alignItems: 'center', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', userSelect: 'none' }}>
                    <input
                      type="checkbox"
                      checked={enableWelcomeBubble}
                      onChange={(e) => hasAdvancedFeatures && setEnableWelcomeBubble(e.target.checked)}
                      disabled={!hasAdvancedFeatures}
                      style={{ marginRight: '8px', cursor: hasAdvancedFeatures ? 'pointer' : 'not-allowed', width: '16px', height: '16px' }}
                    />
                    <span style={{ fontSize: '13px', color: '#333' }}>Show welcome message above icon</span>
                  </label>
                </FormGroup>

                {/* Welcome Message */}
                {enableWelcomeBubble && hasAdvancedFeatures && (
                  <>
                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Welcome Message</span>}
                      helperText={`Message to display in the welcome bubble (${welcomeMessage.length}/35 characters)`}
                    >
                      <InputGroup
                        placeholder="Hello! How can I help you?"
                        value={welcomeMessage}
                        onChange={(e) => {
                          const value = e.target.value.slice(0, 35) // Limit to 35 characters
                          setWelcomeMessage(value)
                        }}
                        rightElement={
                          <div style={{
                            padding: '0 8px',
                            fontSize: '11px',
                            color: welcomeMessage.length >= 35 ? '#d9534f' : '#666',
                            alignSelf: 'center'
                          }}>
                            {welcomeMessage.length}/35
                          </div>
                        }
                      />
                    </FormGroup>

                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Display Delay: {welcomeDelay}ms</span>}
                      helperText="Delay before showing the bubble"
                    >
                      <Slider
                        min={0}
                        max={5000}
                        stepSize={500}
                        labelStepSize={2500}
                        value={welcomeDelay}
                        onChange={setWelcomeDelay}
                        showTrackFill
                      />
                    </FormGroup>

                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Typing Speed: {typingSpeed}ms</span>}
                      helperText="Speed of typing animation (lower = faster)"
                    >
                      <Slider
                        min={30}
                        max={150}
                        stepSize={10}
                        labelStepSize={60}
                        value={typingSpeed}
                        onChange={setTypingSpeed}
                        showTrackFill
                      />
                    </FormGroup>

                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Font Size: {welcomeFontSize}px</span>}
                      helperText="Font size of the welcome message text"
                    >
                      <Slider
                        min={10}
                        max={16}
                        stepSize={1}
                        labelStepSize={2}
                        value={welcomeFontSize}
                        onChange={setWelcomeFontSize}
                        showTrackFill
                      />
                    </FormGroup>

                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Font Style</span>}
                    >
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', userSelect: 'none' }}>
                        <input
                          type="checkbox"
                          checked={welcomeFontBold}
                          onChange={(e) => setWelcomeFontBold(e.target.checked)}
                          style={{ marginRight: '8px', cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{ fontSize: '13px', color: '#333' }}>Make text bold</span>
                      </label>
                    </FormGroup>

                    <FormGroup
                      label={<span style={{ fontSize: '13px', fontWeight: 600, color: '#333' }}>Font Color</span>}
                    >
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <input
                          type="color"
                          value={welcomeFontColor}
                          onChange={(e) => setWelcomeFontColor(e.target.value)}
                          style={{ width: '50px', height: '36px', cursor: 'pointer', border: '1px solid #ccc', borderRadius: '4px' }}
                        />
                        <InputGroup
                          value={welcomeFontColor}
                          onChange={(e) => setWelcomeFontColor(e.target.value)}
                          placeholder="#333333"
                        />
                      </div>
                    </FormGroup>
                  </>
                )}
              </Card>
            </div>

            {/* Action Buttons */}
            <div style={{
              marginTop: '24px',
              paddingTop: '24px',
              borderTop: '1px solid #e1e4e8',
              display: 'flex',
              gap: '12px',
              justifyContent: 'flex-end'
            }}>
              <Button
                text={isSaving ? 'Saving...' : 'Save Changes'}
                onClick={saveConfig}
                intent="primary"
                icon="floppy-disk"
                disabled={isSaving}
                loading={isSaving}
              />
            </div>

            {/* Information Card */}
            <Card
              elevation={Elevation.ONE}
              style={{
                marginTop: '20px',
                padding: '14px',
                backgroundColor: '#E8F4FD',
                borderLeft: '4px solid #2196F3'
              }}
            >
              <div style={{ display: 'flex', gap: '12px' }}>
                <Icon icon="info-sign" iconSize={16} color="#2196F3" style={{ marginTop: '2px', flexShrink: 0 }} />
                <div style={{ fontSize: '12px', color: '#333', lineHeight: '1.6' }}>
                  <strong>Note:</strong> After saving the configuration, the chatbot Icon may take some time to reflect on your website.
                </div>
              </div>
            </Card>
          </>
        )}
      </div>

      {/* Right Column - Fixed Preview */}
      <div style={{
        width: '450px',
        flexShrink: 0,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        <div style={{
          padding: '10px 10px',
          borderBottom: '1px solid #e1e4e8',
          backgroundColor: '#fff'
        }}>
          <h5 style={{ margin: 0, fontSize: '14px', fontWeight: 600, color: '#666', textTransform: 'uppercase' }}>
            Live Preview
          </h5>
        </div>
        <div style={{
          flex: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '10px 10px 10px 10px',
          position: 'relative'
        }}>
          {/* Simulated website background */}
          <div style={{
            position: 'absolute',
            inset: '20px',
            backgroundColor: '#fff',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            border: '1px solid #e1e4e8'
          }}>
            {/* Fake website header */}
            <div style={{
              height: '40px',
              borderBottom: '1px solid #e1e4e8',
              display: 'flex',
              alignItems: 'center',
              padding: '0 16px',
              gap: '8px'
            }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ff5f57' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#febc2e' }} />
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#28c840' }} />
              <div style={{
                flex: 1,
                marginLeft: '12px',
                height: '24px',
                backgroundColor: '#f0f0f0',
                borderRadius: '4px'
              }} />
            </div>
            {/* Fake content lines */}
            <div style={{ padding: '20px' }}>
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '80%' }} />
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '60%' }} />
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', marginBottom: '12px', width: '70%' }} />
              <div style={{ height: '12px', backgroundColor: '#f0f0f0', borderRadius: '4px', width: '50%' }} />
            </div>
          </div>

          {/* Chat widget preview - positioned bottom right */}
          <div style={{
            position: 'absolute',
            bottom: '40px',
            right: '40px',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-end',
            gap: '12px'
          }}>
            {/* Icon preview */}
            <div style={{
              position: 'relative',
              right: `${40 + (80 - iconSize)}px`,
              display: 'inline-block'
            }}>
              {enableRipple && hasAdvancedFeatures && (
                <div
                  style={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: `${(iconSize * rippleRadius) / 100}px`,
                    height: `${(iconSize * rippleRadius) / 100}px`,
                    borderRadius: '50%',
                    border: `2px solid ${borderColor}`,
                    opacity: 0.3,
                    transform: 'translate(-50%, -50%)',
                    animation: 'ripple-animation 1.5s ease-out infinite'
                  }}
                />
              )}
              <div
                style={{
                  width: `${iconSize}px`,
                  height: `${iconSize}px`,
                  borderRadius: `${borderRadius}%`,
                  border: `2px solid ${borderColor}`,
                  backgroundColor: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.4)',
                  overflow: 'hidden',
                  transition: 'all 0.3s ease',
                  position: 'relative',
                  zIndex: 1,
                  cursor: 'pointer'
                }}
              >
                {previewUrl && !imageError ? (
                  <img
                    src={previewUrl}
                    alt="Chat Icon"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover'
                    }}
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <svg width="32" height="32" viewBox="0 0 32 32" fill="none">
                    <path d="M16 2C8.268 2 2 7.641 2 14.5c0 3.891 2.109 7.344 5.391 9.531v5.469l5.172-2.844C14.016 26.891 14.99 27 16 27c7.732 0 14-5.641 14-12.5S23.732 2 16 2z" fill="currentColor" />
                  </svg>
                )}
              </div>
            </div>

            {/* Welcome bubble preview - after icon */}
            {enableWelcomeBubble && hasAdvancedFeatures && (
              <div
                key={`preview-bubble-${welcomeMessage}-${welcomeDelay}-${typingSpeed}-${welcomeFontSize}-${welcomeFontBold}-${welcomeFontColor}-${enableWelcomeBubble}`}
                style={{
                  backgroundColor: '#fff',
                  borderRadius: '8px',
                  padding: '8px 8px',
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
                  border: '2px solid #ff0000',
                  maxWidth: '110px',
                  position: 'relative'
                }}
              >
                <p style={{
                  margin: 0,
                  fontSize: `${welcomeFontSize}px`,
                  color: welcomeFontColor,
                  lineHeight: '1.3',
                  fontWeight: welcomeFontBold ? 600 : 400
                }}>
                  {welcomeMessage}
                </p>
                {/* Bubble tail - pointing up to icon */}
                <div style={{
                  position: 'absolute',
                  top: '-8px',
                  left: '20px',
                  width: '0',
                  height: '0',
                  borderLeft: '8px solid transparent',
                  borderRight: '8px solid transparent',
                  borderBottom: '8px solid #ffffff',
                  filter: 'drop-shadow(0 -2px 2px rgba(0, 0, 0, 0.1))',
                  // borderBottom: '6px solid #ff0000'
                }} />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* CSS Animation */}
      <style>{`
        @keyframes ripple-animation {
          0% {
            transform: translate(-50%, -50%) scale(0.8);
            opacity: 0.5;
          }
          50% {
            opacity: 0.3;
          }
          100% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 0;
          }
        }
      `}</style>
    </div>
  )
}

export default WebchatIconCustomization
